import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import EnhancedTable from '../../components/table';
import Config from '../../config';
import Moment from 'react-moment';
import { Button, Checkbox, Dialog, DialogContent, Toolbar, Typography } from '@mui/material';
import axios from 'axios';
import EventModal from './event_modal';
import { getCompanyNames } from '../../helpers';
import DownloadButton from '../../components/download_button';
import DocLink from '../../components/document_link';

interface TableUrlBuilder {
    filter?: string;
    orderBy?: string;
}

interface EventManagementProps {
    setTitle?: (title: string) => void;
}

// Converted from a class component to an idiomatic function component. Each piece
// of state is its own hook; the `refresh` token that tells the table to reload is
// flipped with a functional updater, and the table's url builder is read from
// state via closure at export time -- no state mirror ref is needed. `selectedRef`
// remains a genuine ref: the table's selection is imperative and never rendered.
// The View button still forces an eventId=null commit with `flushSync` (resetting
// EventModal) before setting the real id, preserving re-open-same-event behavior.
function EventManagement(props: EventManagementProps) {
    const [showError, setShowError] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const [refresh, setRefresh] = useState<boolean>(false);
    const [eventId, setEventId] = useState<number | string | null | undefined>(undefined);
    const [eventViewed, setEventViewed] = useState<boolean | undefined>(undefined);
    const [tableUrlBuilder, setTableUrlBuilder] = useState<TableUrlBuilder | undefined>(undefined);
    const selectedRef = useRef<Array<number | string>>([]);

    const toggleRefresh = () => setRefresh(r => !r);

    useEffect(() => {
        if(props.setTitle) {
            props.setTitle('Events');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onViewChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        var id = e.currentTarget.value;
        var checked = e.currentTarget.checked;
        await markViewed([id], checked);
        toggleRefresh();
    };
    const markViewed = async (ids: Array<number | string>, viewed: boolean) => {
        await axios.get(Config.api+'/odata/Company/Functions.MarkEvents?viewed='+viewed+'&ids='+ids.join(','));
    };
    const setSelected = (ids: Array<number | string>) => {selectedRef.current = ids;};
    const markSelectedViewed = async () => {
        await mark(true);
    };
    const markSelectedUnviewed = async () => {
        await mark(false);
    };
    const mark = async (viewed: boolean) => {
        await markViewed(selectedRef.current, viewed);
        toggleRefresh();
    };
    const getExportConfig = () => {
        let type ='xlsx';
        return {
            href: Config.api + `/odata/Company/Functions.ExportActivityEvents?fileType=${type}&$top=1000` + ((tableUrlBuilder && tableUrlBuilder.filter) || '') + ((tableUrlBuilder && tableUrlBuilder.orderBy) || ''),
            fileName: "events-" + Math.floor(Date.now() / 1000).toString() + '.' + type
        };
    };
    const onError = (error: string) => {
        setShowError(true);
        setError(error);
    };

    var config = {
        url: Config.api + "/odata/Company/VwActivityEvents",
        columns: [
          { id: 'EventDate', type:'date', filterable: true, sortable: true, label: 'Date', template: (value: any) => (<Moment format='MM/DD/YYYY hh:mm A'>{value}</Moment>), width: '12em' },
          { id: 'PartnerCompanyName', filterable: true, label: 'Partner', width: '15em', getOptions: getCompanyNames },
          { id: 'DocId', filterable: true, label: 'ID#', width: '5em', template: (value: any, row: any) => value && row.DocType ? <DocLink docType={row.DocType} id={value}>{value}</DocLink> : '' },
          { id: 'DocType', hidden: true, label: 'Type' },
          { id: 'EventLevel', filterable: true, label: 'Severity', width: '4em' },
          { id: 'Summary', filterable: true, label: 'Summary', allowStringOperators: true },
          { id: 'Viewed', filterable: true, type: 'bool', label: 'Viewed', width: '1em', stopPropagation: 'true', template: (value: any, row: any) => (<Checkbox checked={value} onChange={onViewChange} value={row.Id.toString()} />) },
          { id: 'Id', width: '10em', stopPropagation: 'true', template: (value: any, row: any) => {return (<Button size='small' variant='outlined' onClick={() => {flushSync(() => setEventId(null)); setEventId(value); setEventViewed(row.Viewed)}}>View</Button>);}}
      ],
      order: 'desc',
      orderBy: 'Id',
      keyField: 'Id',
      pageSize: 20,
      pageSizes: [10, 20, 50, 100],
      title: 'Events',
      selectable: true,
      filterInUrl: true,
      refresh: refresh,
      filter: {filters: [{field: "CustomerViewable", operator: 'eq', value: true}]},
      onGetData: (builder: TableUrlBuilder) => setTableUrlBuilder(builder),
      setSelected: setSelected,
      selectedActions: <div style={{display: 'flex'}}>
        <Button variant="text" onClick={markSelectedViewed} style={{minWidth: '11em'}}>Mark Viewed</Button>
        <Button variant="text" onClick={markSelectedUnviewed} style={{minWidth: '12em'}}>Mark Unviewed</Button>
      </div>,
      actions: <div style={{marginTop: 'auto', marginBottom: 'auto', display: 'flex', gap: '1em'}}>
      <DownloadButton onError={onError} disabled={!tableUrlBuilder} size='small' variant='outlined' onClick={getExportConfig}>Export</DownloadButton>
      </div>,
    }
    return (<div>
                <EnhancedTable config={config}/>
                <EventModal eventId={eventId} viewed={eventViewed} onView={() => toggleRefresh()}/>
                <Dialog open={showError} onClose={() => {setShowError(false)}}>
                    <Toolbar className='lbtoolbar'>Error</Toolbar>
                    <DialogContent><Typography>{error}</Typography></DialogContent>
                </Dialog>
            </div>);
}

export default EventManagement;
